import { format } from 'date-fns';
import { useHostAuth } from '../../../shared/ui';
import { AccountPanel } from '../components/account-panel';
import { NoticesPanel } from '../components/notices-panel';
import { RecentQuotesPanel } from '../components/recent-quotes-panel';
import { StartQuotePanel } from '../components/start-quote-panel';
import { useDistributor } from '../use-distributor';
import { PAGE_WIDTH } from '../../../layout/page-width';

function greeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

function firstName(name: string | undefined): string {
  return name?.trim().split(/\s+/)[0] ?? '';
}

/**
 * The distributor portal's landing page.
 *
 * What was here before was the Hi-Level logo, the sentence "Welcome to the iQD
 * Distributor Portal." and two cards whose entire content was a button. None of
 * it knew who was looking at it, so the first act of every visit was to leave.
 *
 * There is no panel chooser here, unlike the internal dashboard. A distributor
 * has one job on this portal — quote a floor — and four panels is the whole of
 * what the portal can tell them about it.
 */
export function DashboardPage() {
  const { isAuthenticated } = useHostAuth();

  // Split rather than an early return inside one component: everything below
  // reads from Apollo, and the signed-out page has nothing to ask for.
  if (!isAuthenticated) {
    return (
      <div className="mx-auto max-w-lg p-12 text-center">
        <h1 className="text-lg font-semibold text-hilevel-slate m-0">iQD Distributor Portal</h1>
        <p className="mt-2 text-sm text-hilevel-gray">
          Sign in to price a mezzanine floor and pick up your quotations.
        </p>
      </div>
    );
  }

  return <Dashboard />;
}

function Dashboard() {
  const { user } = useHostAuth();
  const { distributor, loading, error } = useDistributor();

  return (
    <div className={`${PAGE_WIDTH} p-4`}>
      <header className="flex items-center justify-between gap-4 mb-4">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-hilevel-slate m-0">
            {greeting()}
            {firstName(user?.name) ? `, ${firstName(user?.name)}` : ''}
          </h2>
          <p className="text-xs text-hilevel-gray m-0 mt-0.5">
            {distributor?.name ? `${distributor.name} · ` : ''}
            {format(new Date(), 'EEEE d MMMM yyyy')}
          </p>
        </div>
        {distributor?.logoUrl && (
          <img
            src={distributor.logoUrl}
            alt={distributor.name}
            className="shrink-0 h-10 max-w-[160px] object-contain"
          />
        )}
      </header>

      {error && (
        <p className="mb-3 px-3 py-2 text-xs text-hilevel-red bg-hilevel-red/10 border border-hilevel-red/20 rounded-md">
          Your company details could not be loaded: {error.message}
        </p>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
        <div className="lg:col-span-2 space-y-4">
          <StartQuotePanel />
          <RecentQuotesPanel distributorId={distributor?.id ?? null} distributorLoading={loading} />
        </div>
        <div className="space-y-4">
          <NoticesPanel />
          <AccountPanel distributor={distributor} />
        </div>
      </div>
    </div>
  );
}
